import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../utils/api";
import { ApiError, getApiErrorMessage, reportError } from "../utils/api-error";
import { AUTH_STATUS } from "./auth-status";
/**
 * @typedef {import('@reduxjs/toolkit').AsyncThunk} AsyncThunk
 */

// Maps a caught error to a status for getApiErrorMessage. ApiError with a
// truthy HTTP status keeps its status; ApiError with status 0 (network
// failure, timeout, abort) maps to "network". Anything else is a fault in our
// own code rather than a transport problem, so it is logged and reported as
// "unknown" -- claiming "network" there would send the user chasing their
// router over our bug.
function statusFromError(err) {
    if (!(err instanceof ApiError)) {
        reportError(err, { origin: "auth thunk" });
        return "unknown";
    }
    return err.status || "network";
}

// The TOTP endpoints answer 204 and nothing else on success. Any other 2xx is a
// broken contract, not an HTTP error -- log it and reject as "unknown" so the 2xx
// never reaches consumers that read the status as an error code.
function contractViolation(response, endpoint) {
    reportError(
        new Error(`Expected 204 from ${endpoint}, got ${response.status}`),
        { origin: "auth thunk", endpoint, status: response.status },
    );
    return "unknown";
}

/**
 * @type AsyncThunk<*, {email: string, password: string}, {}>
 */
export const login = createAsyncThunk("auth/login", async (authData, { rejectWithValue }) => {
    try {
        const response = await apiFetch("/v2/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(authData),
        });
        return response.json();
    } catch (err) {
        if (err instanceof ApiError && err.code === "EMAIL_NOT_VERIFIED") {
            return rejectWithValue({
                error: "EMAIL_NOT_VERIFIED",
                email: authData.email,
                message: err.body?.message || "Please verify your email address before logging in",
            });
        }
        return rejectWithValue({ status: statusFromError(err) });
    }
});

export const totpVerify = createAsyncThunk(
    "auth/totp",
    async ({ code }, { rejectWithValue }) => {
        try {
            const response = await apiFetch("/v2/auth/totp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code }),
            });
            if (response.status !== 204) {
                return rejectWithValue({ status: contractViolation(response, "/v2/auth/totp") });
            }
            return { success: true };
        } catch (err) {
            return rejectWithValue({ status: statusFromError(err) });
        }
    },
);

export const totpSetup = createAsyncThunk(
    "auth/setup-totp",
    async (setupData, { rejectWithValue }) => {
        try {
            const response = await apiFetch("/v2/auth/setup-totp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(setupData),
            });
            if (response.status !== 204) {
                return rejectWithValue({
                    status: contractViolation(response, "/v2/auth/setup-totp"),
                });
            }
            return { success: true };
        } catch (err) {
            return rejectWithValue({ status: statusFromError(err) });
        }
    },
);

export const fetchSession = createAsyncThunk("auth/fetchSession", async (_, { rejectWithValue }) => {
    try {
        const response = await apiFetch("/v2/auth/session");
        return response.json();
    } catch (err) {
        // 401 means the session has expired — surface a distinct signal so
        // the slice can flip to LOGGED_OUT without showing an error banner.
        if (err instanceof ApiError && err.status === 401) {
            return rejectWithValue("session-expired");
        }
        throw err;
    }
});

export const logout = createAsyncThunk("auth/logout", async () => {
    // Logout should always result in LOGGED_OUT state regardless of what
    // the backend returns. Swallow fetch failures and 204s alike.
    try {
        const response = await apiFetch("/v2/auth/logout");
        if (response.status === 204) return null;
        return await response.json();
    } catch {
        return null;
    }
});

export const authSlice = createSlice({
    name: "auth",
    initialState: {
        // unknown pending totpPending loggedOut loggedIn emailVerificationRequired
        status: AUTH_STATUS.UNKNOWN,
        error: false,
        errorMessage: "",
        session: {},
        unverifiedEmail: null,
    },
    extraReducers(builder) {
        // Login Async States
        builder.addCase(login.pending, (state) => {
            state.status = AUTH_STATUS.PENDING;
            state.error = false;
            state.errorMessage = "";
            state.unverifiedEmail = null;
        });
        builder.addCase(login.fulfilled, (state) => {
            state.status = AUTH_STATUS.TOTP_PENDING;
            state.errorMessage = "";
            state.error = false;
            state.unverifiedEmail = null;
        });
        builder.addCase(login.rejected, (state, action) => {
            // Check if this is an email verification error (issue #943)
            if (action.payload?.error === "EMAIL_NOT_VERIFIED") {
                state.status = AUTH_STATUS.EMAIL_VERIFICATION_REQUIRED;
                state.errorMessage = action.payload.message || "Please verify your email address before logging in";
                state.unverifiedEmail = action.payload.email;
                state.error = true;
            } else {
                state.status = AUTH_STATUS.LOGGED_OUT;
                const status = action.payload?.status ?? "unknown";
                state.errorMessage = getApiErrorMessage(status, "login");
                state.error = true;
                state.unverifiedEmail = null;
            }
        });

        // TOTP Async States
        builder.addCase(totpVerify.pending, (state) => {
            state.status = "totpVerifyPending";
            state.error = false;
            state.errorMessage = "";
        });
        builder.addCase(totpVerify.fulfilled, (state) => {
            state.status = AUTH_STATUS.LOGGED_IN;
            state.errorMessage = "";
            state.error = false;
        });
        builder.addCase(totpVerify.rejected, (state, action) => {
            state.status = AUTH_STATUS.LOGGED_OUT;
            const status = action.payload?.status ?? "unknown";
            state.errorMessage = getApiErrorMessage(status, "totp");
            state.error = true;
        });

        builder.addCase(totpSetup.pending, (state) => {
            state.status = "totpSetupPending";
            state.error = false;
            state.errorMessage = "";
        });
        builder.addCase(totpSetup.fulfilled, (state) => {
            state.status = AUTH_STATUS.LOGGED_IN;
            state.errorMessage = "";
            state.error = false;
        });
        builder.addCase(totpSetup.rejected, (state, action) => {
            state.status = AUTH_STATUS.LOGGED_OUT;
            const status = action.payload?.status ?? "unknown";
            state.errorMessage = getApiErrorMessage(status, "totp");
            state.error = true;
        });

        // Session Async States
        builder.addCase(fetchSession.pending, (state) => {
            // Only flip to PENDING on the initial UNKNOWN -> PENDING transition.
            // Window focus dispatches fetchSession via useSessionRefresh; if we
            // reset status to PENDING mid-session, AdminWrapper's LoadingScreen
            // guard unmounts the admin tree and destroys unsaved local state
            // (e.g., BuildSurvey's Details/Questions tabs).
            if (state.status === AUTH_STATUS.UNKNOWN) {
                state.status = AUTH_STATUS.PENDING;
            }
        });
        builder.addCase(fetchSession.fulfilled, (state, action) => {
            state.session = action.payload;
            state.status = AUTH_STATUS.LOGGED_IN;
            state.error = false;
            state.errorMessage = "";
        });
        builder.addCase(fetchSession.rejected, (state) => {
            state.status = AUTH_STATUS.LOGGED_OUT;
            state.user = null;
        });

        // Logout Async States
        builder.addCase(logout.fulfilled, (state) => {
            state.status = AUTH_STATUS.LOGGED_OUT;
            state.errorMessage = "";
            state.error = false;
        });
    },
});

export const authSelector = (state) => state[authSlice.name];
export default authSlice.reducer;
